import './index.scss';

// import '@/mui/license';
import React from 'react';
import { Provider } from 'react-redux';
import { HashRouter as Router } from 'react-router';
import dayjs from 'dayjs';

import {
  REACT_APP_BACKUP_PLAYLIST_ORIGIN,
  REACT_APP_BUILD_DATE_TIME,
  REACT_APP_BUILD_ENV,
  REACT_APP_CONFIG_BUCKET,
  REACT_APP_CONFIG_ENV,
  REACT_APP_IRE_PLAYER_WIDGET_ORIGIN,
  REACT_APP_IRE_PLAYER_WIDGET_PART_URL,
  REACT_APP_SOCIAL_SHARING_ORIGIN,
  REACT_APP_TRAFFIC_MANAGER_ORIGIN,
  REACT_APP_VERSION,
} from './modules/common/constants/envs';
import { REACT_APP_MAIN_SCRIPT_TRIGGER } from './modules/common/constants/globals';

import { RootContainers } from './modules/common/helpers';
import { handlePublisherAnchor, runWhenDOMReady } from './modules/common/helpers/dom';
import { getContainerId, setExternalEmbedData } from './modules/common/helpers/embedCodeParams';
import logger from './modules/common/helpers/logger';
import {
  isInSameDomain,
  isWatchInIframe,
  tryPresetInIframeRouter,
  tryPresetSocialRouter,
} from './modules/common/helpers/routes';
import store from './modules/common/helpers/store';
import { setStoreForRequestFn } from '@/modules/common/helpers/request';
import { configFetchStartAction } from '@/modules/common/redux/slices/common';

import AdProvider from '@/modules/common/components/Ad/AdProvider';
import CacheConfig from '@/modules/common/components/CacheProvider/CacheProvider';
import ErrorBoundary from '@/modules/common/components/ErrorBoundary/ErrorBoundary';
import { IsInPopupContext } from '@/modules/common/components/PagePopup/PagePopup';
import ThemeConfig from '@/mui/theme';
import App from './modules/App';
import ScrollObserverProvider from './modules/common/components/ScrollObserver/ScrollObserverProvider';
import GlobalTrigger from './modules/GlobalTrigger';
import { SnackbarGroup } from '@/mui/components';

if (window[REACT_APP_MAIN_SCRIPT_TRIGGER]) {
  window[REACT_APP_MAIN_SCRIPT_TRIGGER]();
}

(function runScript() {
  window[REACT_APP_MAIN_SCRIPT_TRIGGER] = runScript;
  setExternalEmbedData();

  const containerId = getContainerId();

  const run = () => {
    logger.log(
      `App version => ${REACT_APP_VERSION}\n`,
      `App Build Time => ${dayjs(REACT_APP_BUILD_DATE_TIME).format('MMM D, YYYY hh:mm A')}\n`,
      `Container Id => ${containerId}\n`,
      `Build environment => ${REACT_APP_BUILD_ENV}\n`,
      `Config environment => ${REACT_APP_CONFIG_ENV}\n`,
      `Backup Playlist Origin => ${REACT_APP_BACKUP_PLAYLIST_ORIGIN}\n`,
      `Player Widget Origin => ${REACT_APP_IRE_PLAYER_WIDGET_ORIGIN}\n`,
      `Player Widget Path => ${REACT_APP_IRE_PLAYER_WIDGET_PART_URL}\n`,
      `TM => ${REACT_APP_TRAFFIC_MANAGER_ORIGIN}\n`,
      `Config Bucket => ${REACT_APP_CONFIG_BUCKET}\n`,
      `Social Sharing => ${REACT_APP_SOCIAL_SHARING_ORIGIN}\n`,
      `In The Iframe => ${isWatchInIframe}\n`,
      `Same Domain => ${isInSameDomain}`,
    );

    if (!document.getElementById(containerId)) {
      return;
    }

    document.body.style.margin = '0';

    handlePublisherAnchor();
    setStoreForRequestFn(store);

    tryPresetSocialRouter();
    if (isWatchInIframe && isInSameDomain) {
      tryPresetInIframeRouter();
    }

    store.dispatch(configFetchStartAction());

    RootContainers.render(
      <Provider store={store}>
        <Router>
          <GlobalTrigger>
            <AdProvider>
              <CacheConfig>
                <IsInPopupContext.Provider value={false}>
                  <ErrorBoundary>
                    <ThemeConfig>
                      <ScrollObserverProvider>
                        <App />
                        <SnackbarGroup maxSnack={4} />
                      </ScrollObserverProvider>
                    </ThemeConfig>
                  </ErrorBoundary>
                </IsInPopupContext.Provider>
              </CacheConfig>
            </AdProvider>
          </GlobalTrigger>
        </Router>
      </Provider>,
    );
  };

  if (document.getElementById(containerId)) {
    run();
  } else {
    runWhenDOMReady(() => run());
  }
})();
